import React, { useState } from 'react';
import ProfileForm from './ProfileForm';
import './MainMenu.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MainMenu = ({ onViewChange, user, onLogout, onUserUpdate }) => {
  const [showProfile, setShowProfile] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const profileIncomplete = !user.profile?.first_name || !user.profile?.last_name;

  const username = (user.username || '').toLowerCase();
  // Sales trainers see all reps' points but are not reps themselves.
  const isTrainer = !!user.is_trainer || username === 'msarka' || username === 'jcastillo';
  // Reps see the daily logger features. acorvino always keeps rep abilities even
  // though he is a superuser. Older cached sessions (no is_rep flag) default to
  // rep so nobody loses access before re-login. Trainers are never reps.
  const isRep = username === 'acorvino'
    || (!isTrainer && (user.is_rep != null ? user.is_rep : !user.manages_people));
  // Managers/directors (anyone with people under them) get the team views.
  const managesPeople = !!user.manages_people || !!user.is_director || !!user.is_senior_director
    || ['rcarballo', 'slightman', 'jgrant'].includes(username);
  // Field Appointments is the manager's logging tool — only managers (a team of
  // reps, not directors) log them. Directors/seniors only VIEW via Team Appointments.
  const isDirectorRole = !!user.is_director || !!user.is_senior_director
    || ['rcarballo', 'slightman', 'jgrant'].includes(username);
  const isFieldManager = (user.manages_people != null ? user.manages_people : user.is_admin)
    && (!isDirectorRole || username === 'jcastillo');

  // acorvino only: email gerdvin yesterday's blitz report (normally sent the day after a blitz).
  const [sendingBlitz, setSendingBlitz] = useState(false);
  const sendBlitzReport = async () => {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const label = y.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    if (!window.confirm(`Email Gerdvin the blitz report for yesterday, ${label}?`)) return;
    setSendingBlitz(true);
    try {
      const res = await fetch(`${API_URL}/api/blitz-report/send/`, {
        method: 'POST', headers: { 'X-User-ID': String(user.userId) },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Send failed');
      alert(`Blitz report for ${label} sent to ${data.sent_to}.`);
    } catch (e) {
      alert(`Blitz report was NOT sent: ${e.message}`);
    } finally {
      setSendingBlitz(false);
    }
  };

  const handleProfileUpdate = (updatedUser) => {
    // Update the user data in the parent component
    if (onUserUpdate) {
      onUserUpdate(updatedUser);
    }
    console.log('Profile updated:', updatedUser);
  };

  return (
    <div className="main-menu">
      <div className="menu-header">
        <h1>Daily Logger</h1>
        <div className="user-info">
          <div className="user-details">
            <div className="username">Welcome, {user.username}!</div>
            {user.profile && (user.profile.first_name || user.profile.last_name || user.profile.email) && (
              <div className="profile-details">
                {(user.profile.first_name || user.profile.last_name) && (
                  <span className="full-name">
                    {`${user.profile.first_name || ''} ${user.profile.last_name || ''}`.trim()}
                  </span>
                )}
                {user.profile.email && (
                  <span className="email">{user.profile.email}</span>
                )}
              </div>
            )}
          </div>
          <div className="user-actions">
            <button onClick={() => setShowProfile(true)} className="profile-button">
              Profile
            </button>
            <button onClick={() => setShowInstallGuide(true)} className="install-button">
              Install App
            </button>
            <button onClick={onLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {profileIncomplete && !bannerDismissed && (
        <div className="profile-reminder">
          <span>Add your first and last name to your profile so your team can identify you.</span>
          <div className="profile-reminder-actions">
            <button onClick={() => setShowProfile(true)} className="profile-reminder-btn">Set Up Profile</button>
            <button onClick={() => setBannerDismissed(true)} className="profile-reminder-dismiss">✕</button>
          </div>
        </div>
      )}

      <div className="menu-buttons">
        {isRep && (
          <>
            <button
              className="menu-button"
              onClick={() => onViewChange('log')}
            >
              Create/Edit Today's Log
            </button>
            <button
              className="menu-button"
              onClick={() => onViewChange('statistics')}
            >
              View Statistics
            </button>
            <button
              className="menu-button opp-button"
              onClick={() => onViewChange('opp_checker')}
            >
              Opportunity Checker
            </button>
          </>
        )}
        {/* MOTIS on hold (customer info) - acorvino only; backend MOTIS_USERS enforces it */}
        {username === 'acorvino' && (
          <button
            className="menu-button motis-button"
            onClick={() => onViewChange('motis')}
          >
            MOTIS
          </button>
        )}
        {(managesPeople || isTrainer) && (
          <button
            className="menu-button points-button"
            onClick={() => onViewChange('team_points')}
          >
            Team Points
          </button>
        )}
        {user.is_admin && (
          <button
            className="menu-button admin-button"
            onClick={() => onViewChange('admin')}
          >
            Admin Dashboard
          </button>
        )}
        {isFieldManager && (
          <button
            className="menu-button activity-button"
            onClick={() => onViewChange('daily_activity')}
          >
            Daily Activity
          </button>
        )}
        {isFieldManager && (
          <button
            className="menu-button appointments-button"
            onClick={() => onViewChange('manager_appointments')}
          >
            Field Appointments
          </button>
        )}
        {(user.is_director || ['rcarballo', 'slightman', 'jgrant', 'acorvino', 'jcastillo'].includes((user.username || '').toLowerCase())) && (
          <button
            className="menu-button director-appts-button"
            onClick={() => onViewChange('director_appointments')}
          >
            {(user.is_senior_director || ['rcarballo', 'acorvino', 'jcastillo'].includes((user.username || '').toLowerCase())) ? 'All Appointments' : 'Team Appointments'}
          </button>
        )}
        {['acorvino', 'fgonzalez'].includes((user.username || '').toLowerCase()) && (
          <button
            className="menu-button comp-button"
            onClick={() => onViewChange('comp_calculator')}
          >
            Comp Calculator
          </button>
        )}
        {['acorvino', 'fgonzalez'].includes((user.username || '').toLowerCase()) && (
          <button
            className="menu-button mileage-button"
            onClick={() => onViewChange('mileage')}
          >
            Mileage
          </button>
        )}
        {username === 'acorvino' && (
          <button
            className="menu-button blitz-button"
            onClick={sendBlitzReport}
            disabled={sendingBlitz}
          >
            {sendingBlitz ? 'Sending...' : 'Send Blitz Report'}
          </button>
        )}
      </div>

      {showProfile && (
        <ProfileForm
          user={user}
          onClose={() => setShowProfile(false)}
          onUpdate={handleProfileUpdate}  // Pass the handler function
        />
      )}

      {showInstallGuide && (
        <div className="modal-overlay" onClick={() => setShowInstallGuide(false)}>
          <div className="install-guide-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowInstallGuide(false)}>×</button>
            <h2>Install App on iPhone</h2>
            <p className="install-note">Safari Only</p>
            <ol className="install-steps">
              <li>Open <strong>Safari</strong> and go to 120points.com</li>
              <li>Tap the <strong>Share button</strong> <span className="share-icon">↑</span> at the bottom</li>
              <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>Tap <strong>"Add"</strong> in the top right corner</li>
              <li>Done — the app icon will appear on your home screen</li>
            </ol>
            <p className="install-warning">This only works in Safari. Chrome and other browsers on iPhone do not support this feature.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainMenu;
